import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, CalendarCheck, ShoppingBag, UtensilsCrossed } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOHead from "@/components/SEOHead";
import {
  LocationChoiceBlock,
  LocationFaqs,
  LocationFinalCta,
  LocationProofStrip,
  LocationSupportAndReviews,
} from "@/components/LocationSharedBlocks";
import { IndustryCardsSection, IndustryProseSection } from "@/components/IndustrySections";
import { CHOICE_BLOCK, FINAL_CTA, HUB, PROOF_STRIP, SUPPORT_BLOCK, plainText } from "@/data/industriesContent";
import { fadeUp, stagger } from "@/utils/motionVariants";

/**
 * /industries — sector hub (new in handover v3, Sep 2026).
 *
 * Section order is fixed by the approved copy:
 *   hero → grouped industry finder → two customer-choice cards → proof
 *   strip → four ways to take payment → comparing providers → more than one
 *   kind of business (+ not-listed contact link) → named-person support →
 *   five hub FAQs → final CTA.
 *
 * No hub form and no customer-review block, by design. Copy lives in
 * data/industriesContent.js.
 */

const GROUP_ICONS = [UtensilsCrossed, ShoppingBag, CalendarCheck];

export default function IndustriesHubPage() {
  return (
    <div data-testid="industries-hub-page">
      <SEOHead
        rawTitle={HUB.seoTitle}
        description={HUB.metaDescription}
        path={HUB.path}
        faqs={HUB.faqs.map((f) => ({ q: f.q, a: plainText(f.a) }))}
        service={{
          name: HUB.h1,
          description: HUB.metaDescription,
          serviceType: "Merchant Services",
        }}
      />

      {/* 1. Hero */}
      <section className="hero-gradient py-16 md:py-24" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Breadcrumbs className="mb-5" />
            <motion.p
              variants={fadeUp}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-4"
            >
              <Briefcase size={12} aria-hidden="true" />
              {HUB.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-black tracking-tight leading-tight mb-6"
            >
              {HUB.h1}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey max-w-3xl leading-relaxed">
              {HUB.heroBody}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* 2. Grouped industry finder */}
      <section className="py-14 md:py-16 bg-white" data-testid="industries-finder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-3">{HUB.finder.heading}</h2>
            <p className="text-sm text-brand-grey leading-relaxed">{HUB.finder.intro}</p>
          </div>
          <div className="space-y-10">
            {HUB.finder.groups.map((group, gi) => {
              const Icon = GROUP_ICONS[gi];
              return (
                <div key={group.heading}>
                  <h3 className="font-heading text-lg font-bold text-brand-black mb-4 flex items-center gap-2">
                    <Icon size={18} className="text-brand-aqua-dark" aria-hidden="true" />
                    {group.heading}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 list-none p-0 m-0">
                    {group.items.map((item) => (
                      <li key={item.slug}>
                        <Link
                          to={`/industries/${item.slug}`}
                          className="group h-full block bg-gray-50 border border-brand-light-grey rounded-2xl p-5 hover:border-brand-aqua/50 hover:bg-white hover:shadow-md transition-all"
                          data-testid={`industries-finder-${item.slug}`}
                        >
                          <span className="font-heading font-bold text-brand-black mb-1.5 flex items-center gap-1.5">
                            {item.label}
                            <ArrowRight
                              size={14}
                              className="text-brand-aqua opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all"
                            />
                          </span>
                          <span className="block text-sm text-brand-grey leading-relaxed">{item.body}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3 + 4. Customer-choice cards and qualified proof strip */}
      <LocationChoiceBlock content={CHOICE_BLOCK} testId="industries-choice" />
      <LocationProofStrip content={PROOF_STRIP} testId="industries-proof-strip" />

      {/* 5. Four ways to take payment */}
      <IndustryCardsSection section={HUB.options} bg="bg-white" testId="industries-options" />

      {/* 6. Comparing providers */}
      <IndustryProseSection section={HUB.comparing} bg="bg-gray-50" testId="industries-comparing" />

      {/* 7. More than one kind of business + not-listed link */}
      <IndustryProseSection section={HUB.moreThanOne} bg="bg-white" testId="industries-more-than-one" />

      {/* 8. Named-person support */}
      <LocationSupportAndReviews support={SUPPORT_BLOCK} review={null} bg="bg-gray-50" testId="industries-support" />

      {/* 9. Five hub FAQs */}
      <LocationFaqs heading={HUB.faqHeading} faqs={HUB.faqs} testIdPrefix="industries-faq" />

      {/* 10. Final CTA */}
      <LocationFinalCta content={FINAL_CTA} testId="industries-final-cta" />
    </div>
  );
}
